import React, { useState } from "react";
import { logAudit } from "../services/backend";

interface Deadline {
  id: string;
  name: string;
  category: string;
  lastDate: string;
  frequencyMonths: number;
  warnDays: number;
  owner: string;
}

const KEY = "statutory_deadlines";
const EMAIL_KEY = "escalation_email";

const DEFAULTS: Deadline[] = [
  { id: "gas", name: "Gas Safety Certificate", category: "Premises", lastDate: "2025-09-15", frequencyMonths: 12, warnDays: 30, owner: "Maintenance" },
  { id: "elec", name: "Electrical Installation Test (EICR)", category: "Premises", lastDate: "2024-05-01", frequencyMonths: 60, warnDays: 60, owner: "Maintenance" },
  { id: "fire", name: "Fire Alarm Service", category: "Premises", lastDate: "2026-02-01", frequencyMonths: 12, warnDays: 30, owner: "Maintenance" },
  { id: "legionella", name: "Legionella Risk Assessment", category: "Premises", lastDate: "2025-07-01", frequencyMonths: 12, warnDays: 30, owner: "Maintenance" },
  { id: "insurance", name: "Insurance Renewal", category: "Governance", lastDate: "2026-01-01", frequencyMonths: 12, warnDays: 45, owner: "Registered Manager" },
  { id: "dbs", name: "Staff DBS Renewals", category: "People", lastDate: "2023-10-01", frequencyMonths: 36, warnDays: 60, owner: "Registered Manager" },
  { id: "training", name: "Mandatory Training Refreshers", category: "People", lastDate: "2025-09-01", frequencyMonths: 12, warnDays: 45, owner: "Training Lead" },
  { id: "dols", name: "DoLS Register Review", category: "Clinical", lastDate: "2026-07-20", frequencyMonths: 1, warnDays: 7, owner: "Clinical Lead" },
  { id: "cqc", name: "CQC Notification Compliance Check", category: "Governance", lastDate: "2026-07-20", frequencyMonths: 1, warnDays: 7, owner: "Registered Manager" },
];

function load(): Deadline[] {
  try {
    const raw = localStorage.getItem(KEY);
    return raw ? (JSON.parse(raw) as Deadline[]) : DEFAULTS;
  } catch {
    return DEFAULTS;
  }
}

function nextDue(d: Deadline): Date {
  const dt = new Date(d.lastDate);
  dt.setMonth(dt.getMonth() + d.frequencyMonths);
  return dt;
}

function daysUntil(dt: Date): number {
  return Math.ceil((dt.getTime() - Date.now()) / 86400000);
}

export default function Deadlines({ user }: { user?: any }) {
  const [items, setItems] = useState<Deadline[]>(load);
  const [email, setEmail] = useState(localStorage.getItem(EMAIL_KEY) || "hr@roopcottage.co.uk");
  const [form, setForm] = useState<Partial<Deadline>>({});
  const [message, setMessage] = useState("");

  const save = (next: Deadline[]) => {
    setItems(next);
    localStorage.setItem(KEY, JSON.stringify(next));
  };

  const addOrUpdate = () => {
    if (!form.name || !form.lastDate) {
      setMessage("Name and last completed date are required.");
      return;
    }
    const record: Deadline = {
      id: form.id || `dl-${Date.now()}`,
      name: form.name || "",
      category: form.category || "Governance",
      lastDate: form.lastDate || "",
      frequencyMonths: parseInt(form.frequencyMonths as any, 10) || 12,
      warnDays: parseInt(form.warnDays as any, 10) || 30,
      owner: form.owner || "Registered Manager",
    };
    const exists = items.some(i => i.id === record.id);
    save(exists ? items.map(i => (i.id === record.id ? record : i)) : [...items, record]);
    setForm({});
    setMessage(`${record.name} saved.`);
  };

  const markRenewed = (id: string) => {
    save(items.map(i => (i.id === id ? { ...i, lastDate: new Date().toISOString().slice(0, 10) } : i)));
    setMessage("Marked as renewed today.");
  };

  const remove = (id: string) => save(items.filter(i => i.id !== id));

  const rows = items
    .map(i => ({ ...i, due: nextDue(i), days: daysUntil(nextDue(i)) }))
    .sort((a, b) => a.days - b.days);

  const overdue = rows.filter(r => r.days < 0);
  const dueSoon = rows.filter(r => r.days >= 0 && r.days <= r.warnDays);

  const escalate = () => {
    localStorage.setItem(EMAIL_KEY, email);
    const lines = [
      ...overdue.map(r => `OVERDUE: ${r.name} - was due ${r.due.toLocaleDateString()} (${Math.abs(r.days)} days ago) - ${r.owner}`),
      ...dueSoon.map(r => `DUE SOON: ${r.name} - due ${r.due.toLocaleDateString()} (in ${r.days} days) - ${r.owner}`),
    ];
    const body = encodeURIComponent(`Statutory Deadline Escalation - Roop Cottage Residential Care Home\n\n${lines.join("\n") || "All statutory deadlines are under control."}\n\nGenerated by RMACS Deadline Engine.`);
    window.location.href = `mailto:${email}?subject=${encodeURIComponent("URGENT: Statutory Deadline Escalation - Roop Cottage")}&body=${body}`;
    logAudit(user, "ESCALATION", "deadlines", "", `Escalation email sent to ${email}: ${overdue.length} overdue, ${dueSoon.length} due soon`);
    setMessage("Escalation email opened and logged to the audit trail.");
  };

  return (
    <div className="space-y-6 p-6">
      <div>
        <h2 className="text-xl font-semibold text-slate-900">Statutory Deadline & Escalation Engine</h2>
        <p className="text-sm text-slate-600">Every legal deadline tracked automatically. Overdue and due-soon items are flagged and escalated to the owner by email.</p>
      </div>

      <div className="grid gap-3 md:grid-cols-3">
        <div className="rounded-xl border-2 border-red-300 bg-red-50 p-4 text-center"><div className="text-2xl font-bold text-red-700">{overdue.length}</div><div className="text-xs text-red-700">OVERDUE</div></div>
        <div className="rounded-xl border-2 border-amber-300 bg-amber-50 p-4 text-center"><div className="text-2xl font-bold text-amber-700">{dueSoon.length}</div><div className="text-xs text-amber-700">DUE SOON</div></div>
        <div className="rounded-xl border-2 border-green-300 bg-green-50 p-4 text-center"><div className="text-2xl font-bold text-green-700">{rows.length - overdue.length - dueSoon.length}</div><div className="text-xs text-green-700">UNDER CONTROL</div></div>
      </div>

      {message ? <div className="rounded-lg bg-blue-50 p-3 text-sm text-blue-900">{message}</div> : null}

      <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
        <div className="flex flex-wrap items-end gap-2">
          <input value={email} onChange={e => setEmail(e.target.value)} className="w-64 rounded-lg border border-slate-300 px-2 py-1 text-sm" placeholder="Escalation email" />
          <button onClick={escalate} className="rounded-lg bg-red-700 px-4 py-2 text-sm font-medium text-white">Send escalation email</button>
        </div>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
        <h3 className="mb-3 font-semibold text-slate-900">{form.id ? "Edit deadline" : "Add deadline"}</h3>
        <div className="grid gap-3 md:grid-cols-3">
          <input value={form.name || ""} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Name (e.g. LOLLER Inspection)" className="rounded-lg border border-slate-300 px-2 py-1 text-sm" />
          <select value={form.category || "Premises"} onChange={e => setForm(f => ({ ...f, category: e.target.value }))} className="rounded-lg border border-slate-300 px-2 py-1 text-sm">
            <option>Premises</option><option>People</option><option>Clinical</option><option>Governance</option>
          </select>
          <input type="date" value={form.lastDate || ""} onChange={e => setForm(f => ({ ...f, lastDate: e.target.value }))} className="rounded-lg border border-slate-300 px-2 py-1 text-sm" />
          <input type="number" value={form.frequencyMonths || ""} onChange={e => setForm(f => ({ ...f, frequencyMonths: e.target.value as any }))} placeholder="Every X months" className="rounded-lg border border-slate-300 px-2 py-1 text-sm" />
          <input type="number" value={form.warnDays || ""} onChange={e => setForm(f => ({ ...f, warnDays: e.target.value as any }))} placeholder="Warn X days before" className="rounded-lg border border-slate-300 px-2 py-1 text-sm" />
          <input value={form.owner || ""} onChange={e => setForm(f => ({ ...f, owner: e.target.value }))} placeholder="Responsible person" className="rounded-lg border border-slate-300 px-2 py-1 text-sm" />
        </div>
        <div className="mt-3 flex gap-2">
          <button onClick={addOrUpdate} className="rounded-lg bg-green-700 px-4 py-2 text-sm font-medium text-white">Save deadline</button>
          {form.id ? <button onClick={() => setForm({})} className="rounded-lg border border-slate-300 px-4 py-2 text-sm">Cancel</button> : null}
        </div>
      </div>

      <div className="space-y-2">
        {rows.map(r => (
          <div key={r.id} className={`rounded-xl border p-4 shadow-sm ${r.days < 0 ? "border-red-300 bg-red-50" : r.days <= r.warnDays ? "border-amber-300 bg-amber-50" : "border-slate-200 bg-white"}`}>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="font-medium text-slate-900">{r.name} <span className="text-xs text-slate-500">{r.category} | {r.owner}</span></div>
              <div className={`text-sm font-semibold ${r.days < 0 ? "text-red-700" : r.days <= r.warnDays ? "text-amber-700" : "text-green-700"}`}>
                {r.days < 0 ? `OVERDUE by ${Math.abs(r.days)} days` : `Due in ${r.days} days`} - {r.due.toLocaleDateString()}
              </div>
            </div>
            <div className="mt-2 flex gap-2">
              <button onClick={() => markRenewed(r.id)} className="rounded-lg border border-slate-300 px-3 py-1 text-sm">Mark renewed today</button>
              <button onClick={() => setForm(r)} className="rounded-lg border border-slate-300 px-3 py-1 text-sm">Edit</button>
              <button onClick={() => remove(r.id)} className="rounded-lg border border-red-300 px-3 py-1 text-sm text-red-700">Remove</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}